import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  getCountFromServer,
  getAggregateFromServer,
  sum,
  count,
  Timestamp,
  updateDoc,
  serverTimestamp,
  deleteDoc,
  deleteField,
} from "firebase/firestore";
import { auth, db } from "./firebase";
import { Order, OrderStatus, MonthlySnapshot } from "../types";
import { fetchRushdMembershipsForUserIds } from "./customerService";

// Metric-eligible order statuses: "paid" represents "processing" in the UI
const SUCCESSFUL_ORDER_STATUSES: OrderStatus[] = [
  "paid",
  "processed",
  "shipped",
  "delivered",
];

const countOrdersByStatuses = async (
  statuses: OrderStatus[],
  startDate?: Date,
  endDate?: Date
): Promise<number> => {
  const ordersRef = collection(db, "orders");

  const queries = statuses.map(async (status) => {
    try {
      let statusQuery = query(ordersRef, where("status", "==", status));

      if (startDate) {
        statusQuery = query(statusQuery, where("createdAt", ">=", startDate));
      }
      if (endDate) {
        statusQuery = query(statusQuery, where("createdAt", "<=", endDate));
      }

      const snapshot = await getCountFromServer(statusQuery);
      return snapshot.data().count;
    } catch (error) {
      console.error(`Error counting orders for status ${status}:`, error);
      return 0;
    }
  });

  const counts = await Promise.all(queries);
  return counts.reduce((sum, count) => sum + count, 0);
};

// Pagination result type
export interface PaginatedOrdersResult {
  orders: Order[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
  total?: number;
}

// Search result type
export interface SearchOrdersResult {
  orders: Order[];
  total: number;
}

const attachRushdMembershipsToOrders = async (orders: Order[]): Promise<void> => {
  if (orders.length === 0) {
    return;
  }

  const userIds = Array.from(
    new Set(
      orders
        .filter(order => !order.rushdMembership)
        .map(order => order.userId)
        .filter(Boolean)
    )
  );

  if (userIds.length === 0) {
    return;
  }

  const membershipMap = await fetchRushdMembershipsForUserIds(userIds);

  orders.forEach(order => {
    if (order.rushdMembership) {
      return;
    }
    const membership = membershipMap.get(order.userId);
    if (membership) {
      (order.customer as any).rushdMembership = membership;
    }
  });
};

/**
 * Helper function to process order data consistently
 */
const processOrderData = (doc: QueryDocumentSnapshot<DocumentData>): Order => {
  const data = doc.data();

  // Helper function to convert Firestore timestamp to Date
  const convertTimestamp = (timestamp: any): Date => {
    if (!timestamp) {
      return new Date();
    }

    // Handle Firestore Timestamp objects
    if (
      timestamp &&
      typeof timestamp === "object" &&
      timestamp.toDate &&
      typeof timestamp.toDate === "function"
    ) {
      return timestamp.toDate();
    }

    // Handle regular Date objects
    if (timestamp instanceof Date) {
      return timestamp;
    }

    // Handle string dates
    if (typeof timestamp === "string") {
      const date = new Date(timestamp);
      return isNaN(date.getTime()) ? new Date() : date;
    }

    // Handle number timestamps
    if (typeof timestamp === "number") {
      return new Date(timestamp);
    }

    // Fallback to current date
    return new Date();
  };

  return {
    id: doc.id,
    orderNumber: data.orderNumber || doc.id, // Fallback to id for legacy orders
    userId: data.userId || "",
    items: data.items || [],
    customer: {
      id: data.customer?.id || "",
      name: data.customer?.name || "Unknown Customer",
      email: data.customer?.email || "",
      phone: data.customer?.phone || "",
      addresses: data.customer?.addresses || [],
      address: data.customer?.address || null,
      orderHistory: data.customer?.orderHistory || [],
      totalSpent: data.customer?.totalSpent || 0,
      lastOrderDate: data.customer?.lastOrderDate
        ? convertTimestamp(data.customer.lastOrderDate)
        : undefined,
      notificationPreferences: data.customer?.notificationPreferences || {
        email: true,
        sms: false,
        orderUpdates: true,
        promotions: false,
        newsletter: false,
      },
      createdAt: data.customer?.createdAt
        ? convertTimestamp(data.customer.createdAt)
        : new Date(),
    },
    shipping: data.shipping || {
      address: {
        id: "",
        type: "shipping",
        firstName: "",
        lastName: "",
        address1: "",
        city: "",
        state: "",
        zipCode: "",
        country: "",
        isDefault: false,
      },
      method: "",
      cost: 0,
      estimatedDelivery: new Date(),
    },
    payment: data.payment || {
      method: "stripe",
      transactionId: "",
      amount: 0,
      currency: "USD",
      status: "pending",
    },
    status: data.status || "pending",
    tracking: data.tracking,
    refund: data.refund,
    discount: data.discount,
    rushdMembership: data.rushdMembership,
    subtotal: data.subtotal || 0,
    total: data.total || 0,
    notes: data.notes || "",
    packed: data.packed || false,
    inProcurement: data.inProcurement || false,
    procurementBatchId: data.procurementBatchId || null,
    createdAt: convertTimestamp(data.createdAt),
    updatedAt: convertTimestamp(data.updatedAt),
    // Include currency metadata
    purchaseCurrency: data.purchaseCurrency,
    exchangeRateSnapshot: data.exchangeRateSnapshot,
    displayTotal: data.displayTotal,
  } as Order;
};

/**
 * Get the total count of orders
 */
export const getOrderCount = async (
  searchQuery?: string,
  status?: OrderStatus | "All"
): Promise<number> => {
  try {
    console.log("getOrderCount called with:", { searchQuery, status });

    const ordersCollection = collection(db, "orders");
    let countQuery = query(ordersCollection);
    // Add status filter if specified, otherwise exclude pending orders
    // Add status filter if specified
    if (status && status !== "All") {
      countQuery = query(ordersCollection, where("status", "==", status));
    } else {
      // Exclude pending orders by default when no specific status is requested
      countQuery = query(ordersCollection, where("status", "!=", "pending"));
    }

    const snapshot = await getCountFromServer(countQuery);
    const count = snapshot.data().count;
    console.log("Order count result:", count);

    return count;
  } catch (error) {
    console.error("Error getting order count:", error);
    throw new Error("Failed to get order count");
  }
};

/**
 * Fetch orders with pagination support
 */
export const fetchOrdersPaginated = async (
  pageSize: number = 20,
  lastDoc: QueryDocumentSnapshot<DocumentData> | null = null,
  searchQuery?: string,
  status?: OrderStatus | "All"
): Promise<PaginatedOrdersResult> => {
  try {
    console.log("fetchOrdersPaginated called with:", {
      pageSize,
      lastDoc: !!lastDoc,
      searchQuery,
      status,
    });

    const ordersCollection = collection(db, "orders");

    // Build base query - order by createdAt descending to show newest first
    let orderQuery = query(ordersCollection, orderBy("createdAt", "desc"));

    // Add status filter if specified, otherwise exclude pending orders
    if (status && status !== "All") {
      orderQuery = query(
        ordersCollection,
        where("status", "==", status),
        orderBy("createdAt", "desc")
      );
    } else {
      // Exclude pending orders by default when no specific status is requested
      orderQuery = query(
        ordersCollection,
        where("status", "!=", "pending"),
        orderBy("createdAt", "desc")
      );
    }

    // Add pagination
    if (lastDoc) {
      orderQuery = query(orderQuery, startAfter(lastDoc));
    }

    orderQuery = query(orderQuery, limit(pageSize));

    console.log("Executing Firestore query...");
    const snapshot = await getDocs(orderQuery);
    console.log("Firestore query result:", {
      docsCount: snapshot.docs.length,
      empty: snapshot.empty,
    });

    const orders = snapshot.docs.map(processOrderData);
    await attachRushdMembershipsToOrders(orders);
    console.log("Processed orders:", orders.length);

    const hasMore = snapshot.docs.length === pageSize;
    const lastDocument =
      snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;

    return {
      orders: orders,
      lastDoc: lastDocument,
      hasMore,
    };
  } catch (error) {
    console.error("Error fetching orders:", error);
    throw new Error("Failed to fetch orders");
  }
};

/**
 * Search orders by order number, customer name, customer email, or customer address
 */
export const searchOrders = async (
  searchQuery: string,
  status?: OrderStatus | "All"
): Promise<SearchOrdersResult> => {
  try {
    const ordersCollection = collection(db, "orders");
    let orderQuery = query(ordersCollection, orderBy("createdAt", "desc"));

    // Add status filter if specified, otherwise exclude pending orders
    if (status && status !== "All") {
      orderQuery = query(
        ordersCollection,
        where("status", "==", status),
        orderBy("createdAt", "desc")
      );
    } else {
      // Exclude pending orders by default when no specific status is requested
      orderQuery = query(
        ordersCollection,
        where("status", "!=", "pending"),
        orderBy("createdAt", "desc")
      );
    }

    const snapshot = await getDocs(orderQuery);
    const allOrders = snapshot.docs.map(processOrderData);
    await attachRushdMembershipsToOrders(allOrders);

    // Filter by search query
    const searchLower = searchQuery.toLowerCase();
    const filteredOrders = allOrders.filter((order) => {
      // Search by order ID
      if (order.id.toLowerCase().includes(searchLower)) {
        return true;
      }

      // Search by customer name
      if (order.customer.name.toLowerCase().includes(searchLower)) {
        return true;
      }

      // Search by customer email
      if (order.customer.email.toLowerCase().includes(searchLower)) {
        return true;
      }

      // Check customer address
      if (order.customer.address) {
        const addr = order.customer.address;
        const fullAddress = `${addr.line1 || ""} ${addr.line2 || ""} ${
          addr.city
        } ${addr.state} ${addr.postal_code}`.toLowerCase();
        if (fullAddress.includes(searchLower)) {
          return true;
        }
      }

      // Check customer addresses array
      if (order.customer.addresses && order.customer.addresses.length > 0) {
        for (const addr of order.customer.addresses) {
          const fullAddress = `${addr.line1 || ""} ${addr.line2 || ""} ${
            addr.city
          } ${addr.state} ${addr.zipCode}`.toLowerCase();
          if (fullAddress.includes(searchLower)) {
            return true;
          }
        }
      }

      return false;
    });

    return {
      orders: filteredOrders,
      total: filteredOrders.length,
    };
  } catch (error) {
    console.error("Error searching orders:", error);
    throw new Error("Failed to search orders");
  }
};

/**
 * Fetch a single order by ID
 */
export const fetchOrderById = async (
  orderId: string
): Promise<Order | null> => {
  try {
    const orderDoc = doc(db, "orders", orderId);
    const snapshot = await getDoc(orderDoc);

    if (!snapshot.exists()) {
      return null;
    }

    const order = processOrderData(snapshot);
    await attachRushdMembershipsToOrders([order]);
    return order;
  } catch (error) {
    console.error("Error fetching order:", error);
    throw new Error("Failed to fetch order");
  }
};

/**
 * Update order status with optional notes
 */
export const updateOrderStatus = async (
  orderId: string,
  newStatus: OrderStatus,
  notes?: string
): Promise<void> => {
  try {
    console.log("updateOrderStatus called with:", {
      orderId,
      newStatus,
      notes,
    });

    const orderDoc = doc(db, "orders", orderId);

    // Prepare update data
    const updateData: any = {
      status: newStatus,
      updatedAt: serverTimestamp(),
    };

    // Add status change to order history if notes are provided
    if (notes) {
      // First, get the current order to preserve existing history
      const currentOrderSnap = await getDoc(orderDoc);
      if (currentOrderSnap.exists()) {
        const currentData = currentOrderSnap.data();
        const existingHistory = currentData.statusHistory || [];

        // Add new status change to history - use regular Date instead of serverTimestamp()
        // Firebase doesn't support serverTimestamp() inside arrays
        updateData.statusHistory = [
          ...existingHistory,
          {
            status: newStatus,
            notes: notes,
            timestamp: new Date(), // Use regular Date instead of serverTimestamp()
            updatedBy: "admin", // Could be replaced with actual admin user ID
          },
        ];
      }

      // Also update the main notes field if provided
      updateData.notes = notes;
    }

    console.log("Updating order with data:", updateData);
    await updateDoc(orderDoc, updateData);
    console.log("Order status updated successfully");
  } catch (error) {
    console.error("Error updating order status:", error);
    throw new Error("Failed to update order status");
  }
};

/**
 * Update or clear the top-level note for an order
 */
export const updateOrderNotes = async (
  orderId: string,
  note: string
): Promise<void> => {
  try {
    const orderDoc = doc(db, "orders", orderId);
    const trimmedNote = note?.trim();

    const updateData: any = {
      updatedAt: serverTimestamp(),
    };

    if (trimmedNote) {
      updateData.notes = trimmedNote;
    } else {
      updateData.notes = deleteField();
    }

    await updateDoc(orderDoc, updateData);
  } catch (error) {
    console.error("Error updating order notes:", error);
    throw new Error("Failed to update order notes");
  }
};

/**
 * Mark order as packed
 */
export const packOrder = async (orderId: string): Promise<void> => {
  try {
    console.log("packOrder called with:", { orderId });

    const orderDoc = doc(db, "orders", orderId);

    // Prepare update data
    const updateData: any = {
      packed: true,
      status: 'processed' as OrderStatus,
      updatedAt: serverTimestamp(),
    };

    // Add packing note to order history
    const currentOrderSnap = await getDoc(orderDoc);
    if (currentOrderSnap.exists()) {
      const currentData = currentOrderSnap.data();
      const existingHistory = currentData.statusHistory || [];

      // Add packing event to history
      updateData.statusHistory = [
        ...existingHistory,
        {
          status: 'processed' as OrderStatus,
          notes: 'Order packed and ready for shipping',
          timestamp: new Date(),
          updatedBy: "admin",
          action: "packed"
        },
      ];
    }

    console.log("Updating order with pack data:", updateData);
    await updateDoc(orderDoc, updateData);
    console.log("Order packed successfully");
  } catch (error) {
    console.error("Error packing order:", error);
    throw new Error("Failed to pack order");
  }
};

/**
 * Update order tracking information (legacy function for backward compatibility)
 * Now uses the new backend API for enhanced tracking with TrackTry integration
 */
export const updateOrderTracking = async (
  orderId: string,
  trackingData: {
    carrier: string;
    carrierName?: string;
    trackingNumber: string;
    url?: string;
    status: string;
    estimatedDelivery?: Date;
  }
): Promise<void> => {
  try {
    console.log("updateOrderTracking called with:", { orderId, trackingData });

    // Use the new backend API for manual tracking
    await addManualTracking(orderId, {
      trackingNumber: trackingData.trackingNumber,
      carrier: trackingData.carrier,
      carrierName: trackingData.carrierName,
      carrierCode: trackingData.carrier.toLowerCase().replace(/\s+/g, '-'),
      estimatedDelivery: trackingData.estimatedDelivery,
    });

    // If status is not the default, update it via the status API
    if (trackingData.status && trackingData.status !== 'pending') {
      await updateTrackingStatus(
        orderId, 
        trackingData.status, 
        `Status updated to ${trackingData.status}`
      );
    }

    console.log("Order tracking updated successfully via backend API");
  } catch (error) {
    console.error("Error updating order tracking:", error);
    throw new Error("Failed to update order tracking");
  }
};

/**
 * Process a refund for a cancelled order
 */
export const refundOrder = async (
  orderId: string,
  reason?: string
): Promise<{ success: boolean; refundId: string; message: string }> => {
  try {
    console.log("Refunding order:", { orderId, reason });

    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("You must be signed in to process a refund");
    }

    const idToken = await currentUser.getIdToken();

    // Use the correct environment variable name
    const apiUrl =
      import.meta.env.VITE_API_BASE_URL || "http://localhost:5001/api";

    const response = await fetch(`${apiUrl}/payment/refund-order`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        orderId,
        reason,
      }),
    });

    // Check if response is ok before trying to parse JSON
    if (!response.ok) {
      let errorMessage = "Failed to process refund";

      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch (jsonError) {
        // If JSON parsing fails, use the status text
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }

      throw new Error(errorMessage);
    }

    // Check if response has content before parsing JSON
    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
      throw new Error("Invalid response format from server");
    }

    const result = await response.json();
    console.log("Refund processed successfully:", result);

    return result;
  } catch (error) {
    console.error("Error processing refund:", error);
    throw error;
  }
};

/**
 * Get total count of orders with status 'paid' and payment status 'succeeded'
 */
export const getPaidSucceededOrderCount = async (): Promise<number> => {
  try {
    const ordersRef = collection(db, "orders");

    // Query for orders with status 'paid'
    const statusQuery = query(ordersRef, where("status", "==", "paid"));

    const snapshot = await getDocs(statusQuery);

    // Filter client-side for payment status 'succeeded'
    let count = 0;
    snapshot.forEach((doc) => {
      const order = doc.data();
      if (order.payment?.status === "succeeded") {
        count++;
      }
    });

    return count;
  } catch (error) {
    console.error("Error getting paid succeeded order count:", error);
    return 0;
  }
};

/**
 * Get total count of orders with status 'shipped'
 */
export const getShippedOrderCount = async (): Promise<number> => {
  try {
    const ordersRef = collection(db, "orders");

    // Query for orders with status 'shipped'
    const statusQuery = query(ordersRef, where("status", "==", "shipped"));

    const snapshot = await getCountFromServer(statusQuery);
    return snapshot.data().count;
  } catch (error) {
    console.error("Error getting shipped order count:", error);
    return 0;
  }
};

/**
 * Get total count of orders for current month (month-to-date)
 */
export const getCurrentMonthOrderCount = async (): Promise<number> => {
  try {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59,
      999
    );

    const ordersRef = collection(db, "orders");

    // Count orders in current month with successful payment-related statuses
    return await countOrdersByStatuses(
      SUCCESSFUL_ORDER_STATUSES,
      startOfMonth,
      endOfDay
    );
  } catch (error) {
    console.error("Error getting current month order count:", error);
    return 0;
  }
};

/**
 * Get total refunded revenue from orders for current month (month-to-date)
 */
export const getCurrentMonthRefundedRevenue = async (): Promise<number> => {
  try {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59,
      999
    );

    const ordersRef = collection(db, "orders");

    // Query for refunded orders in current month
    const refundedQuery = query(
      ordersRef,
      where("payment.status", "==", "refunded"),
      where("createdAt", ">=", startOfMonth),
      where("createdAt", "<=", endOfDay)
    );

    const snapshot = await getAggregateFromServer(refundedQuery, {
      totalRefunded: sum("total"),
    });

    return snapshot.data().totalRefunded || 0;
  } catch (error) {
    console.error("Error getting current month refunded revenue:", error);
    return 0;
  }
};

/**
 * Get total revenue from orders for current month (month-to-date)
 */
export const getCurrentMonthOrderRevenue = async (): Promise<number> => {
  try {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59,
      999
    );

    const ordersRef = collection(db, "orders");

    // Query for orders in current month with successful payment-related statuses
    const monthQuery = query(
      ordersRef,
      where("status", "in", SUCCESSFUL_ORDER_STATUSES),
      where("createdAt", ">=", startOfMonth),
      where("createdAt", "<=", endOfDay)
    );

    const snapshot = await getAggregateFromServer(monthQuery, {
      totalRevenue: sum("total"),
    });

    // Get refunded revenue for the same period
    const refundedRevenue = await getCurrentMonthRefundedRevenue();

    // Subtract refunded revenue from total revenue
    return (snapshot.data().totalRevenue || 0) - refundedRevenue;
  } catch (error) {
    console.error("Error getting current month order revenue:", error);
    return 0;
  }
};

/**
 * Get order count for same period in previous month
 */
export const getPreviousMonthSamePeriodOrderCount =
  async (): Promise<number> => {
    try {
      const now = new Date();
      const currentDay = now.getDate();

      // Calculate previous month dates
      const prevMonth = now.getMonth() - 1;
      const prevYear =
        prevMonth < 0 ? now.getFullYear() - 1 : now.getFullYear();
      const adjustedPrevMonth = prevMonth < 0 ? 11 : prevMonth;

      // Get the number of days in the previous month
      const daysInPrevMonth = new Date(
        prevYear,
        adjustedPrevMonth + 1,
        0
      ).getDate();

      // Use the minimum of current day or days available in previous month
      const comparisonDay = Math.min(currentDay, daysInPrevMonth);

      const prevMonthStart = new Date(prevYear, adjustedPrevMonth, 1);
      const prevMonthEnd = new Date(
        prevYear,
        adjustedPrevMonth,
        comparisonDay,
        23,
        59,
        59,
        999
      );

      const ordersRef = collection(db, "orders");

      return await countOrdersByStatuses(
        SUCCESSFUL_ORDER_STATUSES,
        prevMonthStart,
        prevMonthEnd
      );
    } catch (error) {
      console.error(
        "Error getting previous month same period order count:",
        error
      );
      return 0;
    }
  };

/**
 * Get refunded revenue for same period in previous month
 */
export const getPreviousMonthSamePeriodRefundedRevenue =
  async (): Promise<number> => {
    try {
      const now = new Date();
      const currentDay = now.getDate();

      // Calculate previous month dates
      const prevMonth = now.getMonth() - 1;
      const prevYear =
        prevMonth < 0 ? now.getFullYear() - 1 : now.getFullYear();
      const adjustedPrevMonth = prevMonth < 0 ? 11 : prevMonth;

      // Get the number of days in the previous month
      const daysInPrevMonth = new Date(
        prevYear,
        adjustedPrevMonth + 1,
        0
      ).getDate();

      // Use the minimum of current day or days available in previous month
      const comparisonDay = Math.min(currentDay, daysInPrevMonth);

      const prevMonthStart = new Date(prevYear, adjustedPrevMonth, 1);
      const prevMonthEnd = new Date(
        prevYear,
        adjustedPrevMonth,
        comparisonDay,
        23,
        59,
        59,
        999
      );

      const ordersRef = collection(db, "orders");

      const refundedQuery = query(
        ordersRef,
        where("payment.status", "==", "refunded"),
        where("createdAt", ">=", prevMonthStart),
        where("createdAt", "<=", prevMonthEnd)
      );

      const snapshot = await getAggregateFromServer(refundedQuery, {
        totalRefunded: sum("total"),
      });

      return snapshot.data().totalRefunded || 0;
    } catch (error) {
      console.error(
        "Error getting previous month same period refunded revenue:",
        error
      );
      return 0;
    }
  };

/**
 * Get revenue for same period in previous month
 */
export const getPreviousMonthSamePeriodOrderRevenue =
  async (): Promise<number> => {
    try {
      const now = new Date();
      const currentDay = now.getDate();

      // Calculate previous month dates
      const prevMonth = now.getMonth() - 1;
      const prevYear =
        prevMonth < 0 ? now.getFullYear() - 1 : now.getFullYear();
      const adjustedPrevMonth = prevMonth < 0 ? 11 : prevMonth;

      // Get the number of days in the previous month
      const daysInPrevMonth = new Date(
        prevYear,
        adjustedPrevMonth + 1,
        0
      ).getDate();

      // Use the minimum of current day or days available in previous month
      const comparisonDay = Math.min(currentDay, daysInPrevMonth);

      const prevMonthStart = new Date(prevYear, adjustedPrevMonth, 1);
      const prevMonthEnd = new Date(
        prevYear,
        adjustedPrevMonth,
        comparisonDay,
        23,
        59,
        59,
        999
      );

      const ordersRef = collection(db, "orders");

      const monthQuery = query(
        ordersRef,
        where("status", "in", SUCCESSFUL_ORDER_STATUSES),
        where("createdAt", ">=", prevMonthStart),
        where("createdAt", "<=", prevMonthEnd)
      );

      const snapshot = await getAggregateFromServer(monthQuery, {
        totalRevenue: sum("total"),
      });

      // Get refunded revenue for the same period
      const refundedRevenue = await getPreviousMonthSamePeriodRefundedRevenue();

      // Subtract refunded revenue from total revenue
      return (snapshot.data().totalRevenue || 0) - refundedRevenue;
    } catch (error) {
      console.error(
        "Error getting previous month same period order revenue:",
        error
      );
      return 0;
    }
  };

/**
 * Get order statistics with comparison to previous month
 */
export const getOrderStatisticsWithComparison = async (): Promise<{
  current: {
    paidSucceededOrders: number;
    shippedOrders: number;
    totalOrders: number;
    totalRevenue: number;
  };
  previous: MonthlySnapshot | null;
  percentageChanges: {
    paidSucceededOrders: number;
    shippedOrders: number;
    totalOrders: number;
    totalRevenue: number;
  };
}> => {
  try {
    console.log("Getting order statistics with same-period comparison...");

    // Get current statistics and previous same-period statistics in parallel
    const [
      paidSucceededOrders,
      shippedOrders,
      currentTotalOrders,
      currentTotalRevenue,
      previousTotalOrders,
      previousTotalRevenue,
    ] = await Promise.all([
      getPaidSucceededOrderCount(),
      getShippedOrderCount(),
      getCurrentMonthOrderCount(),
      getCurrentMonthOrderRevenue(),
      getPreviousMonthSamePeriodOrderCount(),
      getPreviousMonthSamePeriodOrderRevenue(),
    ]);

    const current = {
      paidSucceededOrders,
      shippedOrders,
      totalOrders: currentTotalOrders,
      totalRevenue: currentTotalRevenue,
    };

    // Create a mock previous snapshot for compatibility (we're not using snapshot anymore)
    const previous: MonthlySnapshot | null =
      previousTotalOrders > 0 || previousTotalRevenue > 0
        ? {
            snapshotId: "same-period-comparison",
            year: new Date().getFullYear(),
            month: new Date().getMonth(),
            totalProducts: 0,
            lowStockProducts: 0,
            outOfStockProducts: 0,
            totalInventoryValue: 0,
            totalOrders: previousTotalOrders,
            totalRevenue: previousTotalRevenue,
            totalCustomers: 0,
            createdAt: new Date(),
          }
        : null;

    // Calculate percentage changes using same-period comparison
    const percentageChanges = {
      paidSucceededOrders: 0, // We don't have historical data for this yet
      shippedOrders: 0, // We don't have historical data for this yet
      totalOrders: calculatePercentageChange(
        currentTotalOrders,
        previousTotalOrders
      ),
      totalRevenue: calculatePercentageChange(
        currentTotalRevenue,
        previousTotalRevenue
      ),
    };

    console.log("Same-period comparison results:", {
      current,
      previous: {
        totalOrders: previousTotalOrders,
        totalRevenue: previousTotalRevenue,
      },
      percentageChanges,
    });

    return {
      current,
      previous,
      percentageChanges,
    };
  } catch (error) {
    console.error(
      "Error getting order statistics with same-period comparison:",
      error
    );
    throw error;
  }
};

/**
 * Get total count of orders excluding pending orders
 */
export const getTotalOrderCount = async (): Promise<number> => {
  try {
    const ordersRef = collection(db, "orders");

    // Count orders with successful payment-related statuses
    return await countOrdersByStatuses(SUCCESSFUL_ORDER_STATUSES);
  } catch (error) {
    console.error("Error getting total order count:", error);
    return 0;
  }
};

/**
 * Get total revenue from orders excluding pending orders
 */
export const getTotalOrderRevenue = async (): Promise<number> => {
  try {
    const ordersRef = collection(db, "orders");

    // Sum revenue for orders with successful payment-related statuses
    const statusQuery = query(
      ordersRef,
      where("status", "in", SUCCESSFUL_ORDER_STATUSES)
    );

    const snapshot = await getAggregateFromServer(statusQuery, {
      totalRevenue: sum("total"),
    });

    return snapshot.data().totalRevenue || 0;
  } catch (error) {
    console.error("Error getting total order revenue:", error);
    return 0;
  }
};

/**
 * Get a monthly snapshot by year and month
 */
export const getMonthlySnapshot = async (
  year: number,
  month: number
): Promise<MonthlySnapshot | null> => {
  try {
    const snapshotId = `${year}-${month.toString().padStart(2, "0")}`;
    console.log("Getting monthly snapshot:", snapshotId);

    const snapshotDoc = doc(db, "monthly_snapshots", snapshotId);
    const snapshot = await getDoc(snapshotDoc);

    if (!snapshot.exists()) {
      console.log("Monthly snapshot not found:", snapshotId);
      return null;
    }

    const data = snapshot.data();
    return {
      snapshotId: data.snapshotId,
      year: data.year,
      month: data.month,
      totalProducts: data.totalProducts,
      lowStockProducts: data.lowStockProducts,
      outOfStockProducts: data.outOfStockProducts,
      totalInventoryValue: data.totalInventoryValue,
      totalOrders: data.totalOrders || 0,
      totalRevenue: data.totalRevenue || 0,
      totalCustomers: data.totalCustomers || 0,
      createdAt: data.createdAt.toDate
        ? data.createdAt.toDate()
        : data.createdAt,
    };
  } catch (error) {
    console.error("Error getting monthly snapshot:", error);
    return null;
  }
};

/**
 * Get the previous month's snapshot for comparison
 */
export const getPreviousMonthSnapshot =
  async (): Promise<MonthlySnapshot | null> => {
    try {
      const now = new Date();
      let year = now.getFullYear();
      let month = now.getMonth(); // Current month (0-indexed)

      // Go to previous month
      if (month === 0) {
        month = 12;
        year -= 1;
      }

      return await getMonthlySnapshot(year, month);
    } catch (error) {
      console.error("Error getting previous month snapshot:", error);
      return null;
    }
  };

/**
 * Calculate percentage change between current and previous values
 */
export const calculatePercentageChange = (
  current: number,
  previous: number
): number => {
  if (previous === 0) {
    return current > 0 ? 100 : 0; // If previous was 0 and current > 0, show 100% increase
  }
  return ((current - previous) / previous) * 100;
};

/**
 * Get daily revenue data for the past 7 days
 */
export const getDailyRevenueData = async (): Promise<
  Array<{ date: string; revenue: number; orders: number }>
> => {
  try {
    console.log("Getting daily revenue data for past 7 days...");

    const ordersRef = collection(db, "orders");
    const dailyData = [];

    // Get data for each of the past 7 days
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const startOfDay = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        0,
        0,
        0,
        0
      );
      const endOfDay = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        23,
        59,
        59,
        999
      );

      // Query for orders on this specific day with successful payment-related statuses
      const dayQuery = query(
        ordersRef,
        where("status", "in", SUCCESSFUL_ORDER_STATUSES),
        where("createdAt", ">=", startOfDay),
        where("createdAt", "<=", endOfDay)
      );

      // Query for refunded orders on this specific day
      const refundedQuery = query(
        ordersRef,
        where("payment.status", "==", "refunded"),
        where("createdAt", ">=", startOfDay),
        where("createdAt", "<=", endOfDay)
      );

      const [daySnapshot, refundedSnapshot] = await Promise.all([
        getAggregateFromServer(dayQuery, {
          totalRevenue: sum("total"),
          orderCount: count(),
        }),
        getAggregateFromServer(refundedQuery, {
          totalRefunded: sum("total"),
        }),
      ]);

      const totalRevenue = daySnapshot.data().totalRevenue || 0;
      const refundedRevenue = refundedSnapshot.data().totalRefunded || 0;
      const netRevenue = totalRevenue - refundedRevenue;

      dailyData.push({
        date: date.toISOString().split("T")[0],
        revenue: netRevenue,
        orders: daySnapshot.data().orderCount || 0,
      });
    }

    return dailyData;
  } catch (error) {
    console.error("Error getting daily revenue data:", error);
    return [];
  }
};

/**
 * Get count of delivered orders for current month
 */
export const getDeliveredOrderCount = async (): Promise<number> => {
  try {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59,
      999
    );

    const ordersRef = collection(db, "orders");

    const deliveredQuery = query(
      ordersRef,
      where("status", "==", "delivered"),
      where("createdAt", ">=", startOfMonth),
      where("createdAt", "<=", endOfDay)
    );

    const snapshot = await getCountFromServer(deliveredQuery);
    return snapshot.data().count;
  } catch (error) {
    console.error("Error getting delivered order count:", error);
    return 0;
  }
};

/**
 * Get count of shipped orders for current month
 */
export const getShippedOrderStatusCount = async (): Promise<number> => {
  try {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59,
      999
    );

    const ordersRef = collection(db, "orders");

    const shippedQuery = query(
      ordersRef,
      where("status", "==", "shipped"),
      where("createdAt", ">=", startOfMonth),
      where("createdAt", "<=", endOfDay)
    );

    const snapshot = await getCountFromServer(shippedQuery);
    return snapshot.data().count;
  } catch (error) {
    console.error("Error getting shipped order count:", error);
    return 0;
  }
};

/**
 * Get count of processed orders for current month
 */
export const getProcessedOrderCount = async (): Promise<number> => {
  try {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59,
      999
    );

    const ordersRef = collection(db, "orders");

    const processedQuery = query(
      ordersRef,
      where("status", "==", "processed"),
      where("createdAt", ">=", startOfMonth),
      where("createdAt", "<=", endOfDay)
    );

    const snapshot = await getCountFromServer(processedQuery);
    return snapshot.data().count;
  } catch (error) {
    console.error("Error getting processed order count:", error);
    return 0;
  }
};

/**
 * Get count of cancelled orders for current month
 */
export const getCancelledOrderCount = async (): Promise<number> => {
  try {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59,
      999
    );

    const ordersRef = collection(db, "orders");

    const cancelledQuery = query(
      ordersRef,
      where("status", "==", "cancelled"),
      where("createdAt", ">=", startOfMonth),
      where("createdAt", "<=", endOfDay)
    );

    const snapshot = await getCountFromServer(cancelledQuery);
    return snapshot.data().count;
  } catch (error) {
    console.error("Error getting cancelled order count:", error);
    return 0;
  }
};

/**
 * Get count of processing orders for current month (status = 'paid' AND payment.status = 'succeeded')
 * Note: This requires client-side filtering since Firestore doesn't support complex nested queries with aggregation
 */
export const getProcessingOrderCount = async (): Promise<number> => {
  try {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59,
      999
    );

    const ordersRef = collection(db, "orders");

    // Query for orders with status 'paid' in current month
    const paidQuery = query(
      ordersRef,
      where("status", "==", "paid"),
      where("createdAt", ">=", startOfMonth),
      where("createdAt", "<=", endOfDay)
    );

    const snapshot = await getDocs(paidQuery);

    // Filter client-side for payment status 'succeeded'
    let count = 0;
    snapshot.forEach((doc) => {
      const order = doc.data();
      if (order.payment?.status === "succeeded") {
        count++;
      }
    });

    return count;
  } catch (error) {
    console.error("Error getting processing order count:", error);
    return 0;
  }
};

/**
 * Get all order status counts for current month efficiently
 */
export const getOrderStatusCounts = async (): Promise<{
  delivered: number;
  shipped: number;
  cancelled: number;
  processing: number;
  processed: number;
}> => {
  try {
    console.log("Getting current month order status counts...");

    // Run all count queries in parallel for better performance
    const [delivered, shipped, cancelled, processing, processed] = await Promise.all([
      getDeliveredOrderCount(),
      getShippedOrderStatusCount(),
      getCancelledOrderCount(),
      getProcessingOrderCount(),
      getProcessedOrderCount(),
    ]);

    const counts = {
      delivered,
      shipped,
      cancelled,
      processing,
      processed,
    };

    console.log("Current month order status counts:", counts);
    return counts;
  } catch (error) {
    console.error("Error getting order status counts:", error);
    return {
      delivered: 0,
      shipped: 0,
      cancelled: 0,
      processing: 0,
      processed: 0,
    };
  }
};

/**
 * Add manual tracking to an order via backend API
 * This will register the tracking with Ship24 for automatic updates
 */
export const addManualTracking = async (
  orderId: string,
  trackingData: {
    trackingNumber: string;
    carrier: string;
    carrierName?: string;
    carrierCode?: string;
    estimatedDelivery?: Date;
  }
): Promise<void> => {
  try {
    console.log("addManualTracking called with:", { orderId, trackingData });

    const apiUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:5001/api";
    
    const response = await fetch(`${apiUrl}/tracking/add-manual/${orderId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        trackingNumber: trackingData.trackingNumber,
        carrier: trackingData.carrier,
        carrierName: trackingData.carrierName,
        carrierCode: trackingData.carrierCode,
        estimatedDelivery: trackingData.estimatedDelivery?.toISOString(),
      }),
    });

    if (!response.ok) {
      let errorMessage = "Failed to add manual tracking";
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (jsonError) {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log("Manual tracking added successfully:", result);
  } catch (error) {
    console.error("Error adding manual tracking:", error);
    throw error;
  }
};

/**
 * Get tracking information for an order via backend API
 */
export const getOrderTracking = async (orderId: string): Promise<any> => {
  try {
    const apiUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:5001/api";
    
    const response = await fetch(`${apiUrl}/tracking/order/${orderId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null; // No tracking information found
      }
      
      let errorMessage = "Failed to get tracking information";
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (jsonError) {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error getting order tracking:", error);
    throw error;
  }
};

/**
 * Update tracking status manually via backend API
 */
export const updateTrackingStatus = async (
  orderId: string,
  status: string,
  description?: string,
  location?: string
): Promise<void> => {
  try {
    console.log("updateTrackingStatus called with:", { orderId, status, description, location });

    const apiUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:5001/api";
    
    const response = await fetch(`${apiUrl}/tracking/update-status/${orderId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        status,
        description,
        location,
      }),
    });

    if (!response.ok) {
      let errorMessage = "Failed to update tracking status";
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (jsonError) {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log("Tracking status updated successfully:", result);
  } catch (error) {
    console.error("Error updating tracking status:", error);
    throw error;
  }
};

/**
 * Manually mark order as delivered via backend API
 */
export const markOrderDelivered = async (
  orderId: string,
  description?: string,
  location?: string
): Promise<void> => {
  try {
    console.log("markOrderDelivered called with:", { orderId, description, location });

    const apiUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:5001/api";
    
    const response = await fetch(`${apiUrl}/tracking/mark-delivered/${orderId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        description,
        location,
      }),
    });

    if (!response.ok) {
      let errorMessage = "Failed to mark order as delivered";
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (jsonError) {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log("Order marked as delivered successfully:", result);
  } catch (error) {
    console.error("Error marking order as delivered:", error);
    throw error;
  }
};

/**
 * Get all orders with pending tracking (for admin monitoring)
 */
export const getPendingTrackingOrders = async (): Promise<any[]> => {
  try {
    const apiUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:5001/api";
    
    const response = await fetch(`${apiUrl}/tracking/pending`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      let errorMessage = "Failed to get pending tracking orders";
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (jsonError) {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    return result.orders || [];
  } catch (error) {
    console.error("Error getting pending tracking orders:", error);
    throw error;
  }
};

/**
 * General function to update order with any fields
 */
export const updateOrder = async (
  orderId: string,
  updateData: Partial<Order>
): Promise<void> => {
  try {
    console.log("updateOrder called with:", { orderId, updateData });

    const orderDoc = doc(db, "orders", orderId);

    // Add updatedAt timestamp to all updates
    const finalUpdateData = {
      ...updateData,
      updatedAt: serverTimestamp(),
    };

    await updateDoc(orderDoc, finalUpdateData);

    console.log(`Order ${orderId} updated successfully`);
  } catch (error) {
    console.error(`Error updating order ${orderId}:`, error);
    throw error;
  }
};

export const deleteOrder = async (orderId: string): Promise<void> => {
  if (!orderId) {
    throw new Error("Order ID is required");
  }

  try {
    const orderDoc = doc(db, "orders", orderId);
    await deleteDoc(orderDoc);
  } catch (error) {
    console.error(`Error deleting order ${orderId}:`, error);
    throw new Error("Failed to delete order");
  }
};
